/**
 * RoutingPolicyEditor — Configura la política de asignación de la sala.
 *
 * Datos: campo `routingPolicy` en `salas/{salaId}` actualizable vía
 * `portal_actualizarSala`.
 */
import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { httpsCallable } from 'firebase/functions';
import { Sparkles, ShieldCheck, Activity } from 'lucide-react';
import { functions } from '../../config/firebase';
import { useToast } from '../../contexts/ToastContext';
import styles from './RoutingPolicyEditor.module.css';

type Strategy =
  | 'fifo_prioridad'
  | 'round_robin'
  | 'menos_ocupado'
  | 'sticky_provider';

interface RoutingPolicy {
  strategy: Strategy;
  continuityWindowDays?: number;
  skillStrict?: boolean;
  maxConsultasPorMedicoDia?: number;
}

const STRATEGIES: { value: Strategy; label: string; description: string }[] = [
  {
    value: 'fifo_prioridad',
    label: 'FIFO + Prioridad (default)',
    description:
      'El siguiente paciente va al primer médico del roster. Las urgencias saltan la cola.',
  },
  {
    value: 'round_robin',
    label: 'Round-robin',
    description:
      'Asigna al médico que más tiempo lleva sin recibir paciente. Equidad entre médicos.',
  },
  {
    value: 'menos_ocupado',
    label: 'Menos ocupado',
    description:
      'Asigna al médico con menos consultas en las últimas 3h. Balancea carga real.',
  },
  {
    value: 'sticky_provider',
    label: 'Continuidad de cuidado',
    description:
      'Prefiere el médico que vio al paciente recientemente (si está disponible).',
  },
];

/** Tarjeta de ayuda punto por punto (columna derecha), como en Capacidad. */
function HelpItem({
  n,
  titulo,
  children,
}: {
  n: number;
  titulo: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.helpItem}>
      <span className={styles.helpItemNum}>{n}</span>
      <div className={styles.helpItemBody}>
        <div className={styles.helpItemTitle}>{titulo}</div>
        <div className={styles.helpItemText}>{children}</div>
      </div>
    </div>
  );
}

const callActualizarSala = httpsCallable(functions, 'portal_actualizarSala');

export interface RoutingPolicyEditorProps {
  salaId: string;
  initial?: RoutingPolicy;
}

export function RoutingPolicyEditor({ salaId, initial }: RoutingPolicyEditorProps) {
  const { addToast } = useToast();
  const [policy, setPolicy] = useState<RoutingPolicy>(
    initial ?? { strategy: 'fifo_prioridad' },
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) setPolicy(initial);
  }, [initial]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await callActualizarSala({ salaId, routingPolicy: policy });
      addToast('success', 'Política de asignación actualizada');
    } catch (err) {
      addToast(
        'error',
        err instanceof Error ? err.message : 'Error al guardar política',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.editor}>
      <div className={styles.capLayout}>
        {/* ─── Controles (columna izquierda) ─── */}
        <div className={styles.capControls}>
          <div className={styles.section}>
            <div className={styles.sectionHead}>
              <Sparkles size={14} aria-hidden="true" />
              <span>Estrategia de asignación</span>
            </div>
            <div className={styles.options}>
              {STRATEGIES.map((s) => (
                <label
                  key={s.value}
                  className={`${styles.option} ${
                    policy.strategy === s.value ? styles.optionActive : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="strategy"
                    value={s.value}
                    checked={policy.strategy === s.value}
                    onChange={() => setPolicy({ ...policy, strategy: s.value })}
                  />
                  <div>
                    <div className={styles.optionLabel}>{s.label}</div>
                    <div className={styles.optionDesc}>{s.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {policy.strategy === 'sticky_provider' && (
            <div className={styles.section}>
              <label className={styles.fieldLabel}>
                Ventana de continuidad (días hacia atrás)
              </label>
              <input
                type="number"
                min={1}
                max={365}
                className={styles.numInput}
                value={policy.continuityWindowDays ?? 30}
                onChange={(e) =>
                  setPolicy({
                    ...policy,
                    continuityWindowDays: Math.max(
                      1,
                      parseInt(e.target.value) || 30,
                    ),
                  })
                }
              />
              <p className={styles.hint}>
                Si el paciente vio al mismo médico en esta ventana, se le
                re-asigna.
              </p>
            </div>
          )}

          <div className={styles.section}>
            <div className={styles.sectionHead}>
              <Activity size={14} aria-hidden="true" />
              <span>Carga máxima por médico</span>
            </div>
            <label className={styles.fieldLabel}>
              Consultas máximas por médico por día
            </label>
            <input
              type="number"
              min={0}
              className={styles.numInput}
              value={policy.maxConsultasPorMedicoDia ?? ''}
              placeholder="Sin límite"
              onChange={(e) => {
                const v = parseInt(e.target.value);
                setPolicy({
                  ...policy,
                  maxConsultasPorMedicoDia: isNaN(v) || v <= 0 ? undefined : v,
                });
              }}
            />
            <p className={styles.hint}>
              0 o vacío = sin límite. Útil para prevenir burnout.
            </p>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHead}>
              <ShieldCheck size={14} aria-hidden="true" />
              <span>Skill matching</span>
              <span className={styles.soonBadge}>Próximamente</span>
            </div>
            <label className={`${styles.toggleRow} ${styles.toggleRowDisabled}`}>
              <input
                type="checkbox"
                disabled
                checked={policy.skillStrict === true}
                aria-describedby="skillStrictHint"
              />
              <div>
                <div className={styles.optionLabel}>Modo estricto</div>
                <div className={styles.optionDesc} id="skillStrictHint">
                  Rechazará o hará fallback según los skills del médico (idioma,
                  edad, sub-especialidad). Requiere configurar los skills de los
                  médicos y los requisitos de la sala — próximamente.
                </div>
              </div>
            </label>
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.btn}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Guardando…' : 'Guardar política'}
            </button>
          </div>
        </div>

        {/* ─── Ayuda (columna derecha, sticky) ─── */}
        <aside className={styles.capHelp} aria-label="Ayuda: política de asignación">
          <div className={styles.helpGeneral}>
            <div className={styles.helpGeneralTitle}>
              ¿Cómo se elige al siguiente médico?
            </div>
            <p>
              Define cómo el sistema elige al siguiente médico cuando entran
              pacientes a la cola de esta sala. Cada sala puede tener su propia
              estrategia. Los cambios aplican a las próximas asignaciones
              automáticas.
            </p>
          </div>

          <HelpItem n={1} titulo="Estrategia de asignación">
            <ul>
              <li>
                <strong>FIFO + Prioridad (default):</strong> el paciente que
                lleva más tiempo esperando va al primer médico disponible del
                roster; las urgencias (prioridad alta) saltan la cola.
                Recomendada para la mayoría de salas.
              </li>
              <li>
                <strong>Round-robin:</strong> reparte de forma pareja; asigna al
                médico que más tiempo lleva sin recibir un paciente. Útil para
                equidad de carga entre médicos.
              </li>
              <li>
                <strong>Menos ocupado:</strong> asigna al médico con menos
                pacientes atendidos hoy (ponderado por su peso/dedicación).
                Balancea la carga real del día.
              </li>
              <li>
                <strong>Continuidad de cuidado:</strong> si el paciente fue
                atendido por un médico de esta sala dentro de la ventana
                configurada, se le vuelve a asignar ese médico (si está
                disponible); si no, cae a FIFO + Prioridad. Mejora el
                seguimiento clínico.
              </li>
            </ul>
          </HelpItem>

          <HelpItem n={2} titulo="Ventana de continuidad">
            Solo aplica con la estrategia <em>Continuidad</em>: número de días
            hacia atrás para buscar al médico previo del paciente. Ej. 30 =
            considera consultas del último mes. Por defecto 30.
          </HelpItem>

          <HelpItem n={3} titulo="Carga máxima por médico">
            Tope de consultas que un médico puede recibir por auto-asignación en
            el día. Al alcanzarlo, deja de recibir pacientes automáticos (siguen
            con los demás médicos). 0 o vacío = sin límite. Útil para prevenir
            sobrecarga/burnout.
          </HelpItem>

          <HelpItem n={4} titulo="Skill matching (modo estricto) — próximamente">
            Rechazará o hará fallback según los skills del médico (idioma, edad,
            sub-especialidad). Requiere configurar los skills de los médicos y
            los requisitos de la sala — próximamente.
          </HelpItem>
        </aside>
      </div>
    </div>
  );
}
